/**
 * @file Brand marks for the portfolio's Past collaborations grid (EPD-10121).
 *
 * A brand with a resolved logo (tier 1, EPD-12217) renders as that logo; the
 * logo URL is looked up server-side from the brand name (AspireX `brandLogos`,
 * Brandfetch) and arrives on the highlight as `brandLogoUrl`. Every other
 * brand — no confident match, or the image fails to load — renders as a
 * wordmark: its name in the theme heading font. The grid renders by `kind`.
 *
 * The prototype's hand-styled per-brand wordmarks (tier 2) were dropped: logos
 * replace them, and a curated list would need someone to maintain it.
 */

const HEADING_FONT = "var(--pf-font-heading)";

/** A rendered type setting for a brand name. */
export interface Wordmark {
  kind: "wordmark";
  /** The brand's full name, for the accessible name. */
  name: string;
  /** What is drawn in the cell. */
  text: string;
  fontFamily: string;
  fontWeight: number;
  fontStyle: "normal" | "italic";
  textTransform: "none" | "uppercase" | "lowercase";
  letterSpacing: string;
}

/** A resolved brand logo, with the wordmark to draw if every image fails. */
export interface LogoMark {
  kind: "logo";
  /** The brand's full name, for the image's alt text. */
  name: string;
  /**
   * Image URLs (http(s)) to try in order: the brand's full logo first, then
   * the stored mark it was derived from. The wordmark is drawn once all fail.
   * Never empty: a brand with no image is a `Wordmark`, not a `LogoMark`.
   */
  sources: readonly [string, ...string[]];
  fallback: Wordmark;
}

/** Everything the grid can draw. */
export type BrandMark = Wordmark | LogoMark;

/** The brand name in the theme heading font, set plainly. */
export function genericWordmark(name: string): Wordmark {
  return {
    kind: "wordmark",
    name,
    text: name,
    fontFamily: HEADING_FONT,
    fontWeight: 500,
    fontStyle: "normal",
    textTransform: "none",
    letterSpacing: "normal",
  };
}

function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

const BRANDFETCH_HOST = "cdn.brandfetch.io";

/**
 * How light the portfolio background the mark sits on is: `isLight(theme.bg)`
 * (lib/portfolio/theme). Creators pick the background, and some presets are
 * dark (`cosmic-night`, `midnight-bloom`).
 */
export type MarkBackground = "light" | "dark";

/**
 * Brandfetch's `theme` names the logo's own colouring, not the page it sits
 * on: `theme/dark` is the dark mark (black Nike swoosh) for light pages, and
 * `theme/light` the light mark (white swoosh) for dark pages. Checked live
 * against nike.com and glossier.com, 2026-10-18; the default full logo is the
 * `theme/dark` one.
 */
const LOGO_THEME_FOR: Record<MarkBackground, "dark" | "light"> = {
  light: "dark",
  dark: "light",
};

/**
 * The brand's full logo (symbol + wordmark) for a stored Brandfetch Logo Link,
 * coloured to read on `background`, or null for any other URL.
 *
 * `brandLogos` stores Brandfetch's default Logo Link (`/<domain>?c=…`, or the
 * explicit `/domain/<domain>?c=…` base form), which serves the square icon.
 * That suits Creator Discovery's avatars, but the portfolio's cells are wide
 * and a full logo reads better there (Heather, 2026-10-16), so the variant is
 * chosen here rather than in the shared lookup. Unlike the icon, which
 * usually carries its own background, a full logo is a bare mark on
 * transparency, so its colouring must follow the page's. `fallback/404` makes
 * a brand with no such logo 404, so the cell falls back to the icon. Without
 * it Brandfetch answers 200 with its own placeholder image, and the cell
 * would show that instead.
 */
export function brandfetchFullLogoUrl(
  url: string,
  background: MarkBackground = "light",
): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" || parsed.hostname !== BRANDFETCH_HOST) {
    return null;
  }
  const segments = parsed.pathname.split("/").filter(Boolean);
  // Only the base `/<domain>` or `/domain/<domain>` forms; a URL that already
  // names a variant or size is left as stored.
  const isBase =
    segments.length === 1 ||
    (segments.length === 2 && segments[0] === "domain");
  if (!isBase) return null;
  parsed.pathname = `/${segments.join("/")}/fallback/404/theme/${LOGO_THEME_FOR[background]}/type/logo`;
  return parsed.toString();
}

/**
 * Resolve the mark to draw for a brand: its logo when it has a valid http(s)
 * logo URL, otherwise the wordmark. `background` picks the full logo's
 * colouring (see `brandfetchFullLogoUrl`).
 */
export function resolveBrandMark(
  brand: {
    name: string;
    logoUrl?: string | null;
  },
  background: MarkBackground = "light",
): BrandMark {
  const wordmark = genericWordmark(brand.name.trim());
  const src = brand.logoUrl?.trim();
  if (src && isHttpUrl(src)) {
    const fullLogo = brandfetchFullLogoUrl(src, background);
    return {
      kind: "logo",
      name: wordmark.name,
      sources: fullLogo ? [fullLogo, src] : [src],
      fallback: wordmark,
    };
  }
  return wordmark;
}

/** The smallest a wordmark is ever scaled to fit its cell. */
export const MIN_WORDMARK_SCALE = 0.5;

/**
 * Scale that fits a mark of natural width `measured` into `available` px:
 * clamp(available / measured, 0.5, 1). Never scales up; unmeasurable input
 * (zero or non-finite widths, e.g. before layout) leaves it at 1.
 */
export function fitScale(available: number, measured: number): number {
  if (
    !Number.isFinite(available) ||
    !Number.isFinite(measured) ||
    measured <= 0 ||
    available <= 0
  ) {
    return 1;
  }
  return Math.min(1, Math.max(MIN_WORDMARK_SCALE, available / measured));
}

/**
 * Logos are sized to the same visual area, not the same height (EPD-12333).
 * At one height a long wordmark ("lululemon") spans the cell while a compact
 * mark (Target's bullseye over its name) is a speck. Equal area gives every
 * brand about the same visual weight: a wide logo comes out shorter, a compact
 * one taller. The height bounds keep a very tall logo inside the cell and stop
 * a very wide one getting any shorter here; the cell can still narrow a very
 * wide logo (`max-w-full`), which shrinks it below the minimum height.
 */
export const LOGO_TARGET_AREA = 3200; // px², e.g. 80 × 40 for a 2:1 logo
export const LOGO_MIN_HEIGHT = 18;
export const LOGO_MAX_HEIGHT = 56;

/**
 * The display box for a logo of natural size `naturalWidth` × `naturalHeight`:
 * the target area at the image's own aspect ratio, height clamped to
 * [LOGO_MIN_HEIGHT, LOGO_MAX_HEIGHT], width at least 1. Null when the size isn't known (not yet
 * loaded, or a zero-size image), so the caller keeps its default sizing.
 */
export function logoBox(
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number } | null {
  if (
    !Number.isFinite(naturalWidth) ||
    !Number.isFinite(naturalHeight) ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return null;
  }
  const ratio = naturalWidth / naturalHeight;
  const height = Math.min(
    LOGO_MAX_HEIGHT,
    Math.max(LOGO_MIN_HEIGHT, Math.sqrt(LOGO_TARGET_AREA / ratio)),
  );
  // At least 1px wide, so a very narrow image never collapses to an invisible
  // zero-width box (which wouldn't trigger the image's error fallback either).
  return {
    width: Math.max(1, Math.round(height * ratio)),
    height: Math.round(height),
  };
}
